/* global MOBILE_BREAKPOINT */

import { showLoading, hideLoading } from 'react-redux-loading-bar'
import { cartItemsFetch } from 'reduxModules/cart'
import _ from 'lodash'
import plans from 'components/SubscriptionPlans/plans'
import {
  getUTMDataFromCookies,
  removeUTMPrefixFromKeysIn,
  UTM_KEYS
} from 'reduxModules/utils/utm'

import detectProxyIPCookie from 'reduxModules/utils/proxy'
import {
  getGlobalPromoCookie,
  setGlobalPromoCookie,
  parsePromoCookie,
  storePromoCode,
  activePromotionCode
} from 'reduxModules/utils/promo'
import { getUser } from 'reduxModules/auth'
import authTypes from 'reduxModules/auth/types'

import { cookie } from 'reduxModules/utils/cookie'
import { subscriptionPlansDispatcher } from 'reduxModules/subscriptions/dispatchers'
import { openExternalUserFunnelDialog } from 'reduxModules/users'
import {
  dispatchProxyLogin,
  dispatchOpenAthensLogin
} from '../auth/dispatchers'
import { clearNotification } from '../generic/notifications'
import {
  subscriptionSummaryFetch,
  closeBanner,
  LIFETIME_PLAN_ID
} from '../subscriptions'
import {
  updateDimensions,
  hideNavigation,
  showNavigation,
  hideInnerNavigation,
  showInnerNavigation,
  setIsMobile,
  unsetIsMobile,
  toggleMobileMenu,
  setFullscreen,
  unsetFullscreen,
  setUTM,
  sendUTM,
  validateGlobalPromoCode,
  updateScrollPercentage,
  getCurrentPromotion,
  displayStandardSubscriptionPlans,
  setGlobalPromoSource,
  restoreGlobalPromo
} from './'
import { capturePosthogEvent } from '../../../utils/posthog'
import { toastServerError } from '../../../global/notifications'

export const navHandlers = (dispatch) => ({
  hideGlobalNav() {
    dispatch(hideNavigation())
  },
  showGlobalNav() {
    dispatch(showNavigation())
  }
})

export const innerNavHandlers = (dispatch) => ({
  hideInnerNav() {
    dispatch(hideInnerNavigation())
  },
  showInnerNav() {
    dispatch(showInnerNavigation())
  }
})

export const bannerHandler = (dispatch) => ({
  closeBannerSubscription() {
    dispatch(closeBanner())
  }
})

// SPEC-092: one /get-current-promotion request per page load, shared by the
// App's mount call and the promo fallback, whichever asks first.
let currentPromotionRequest = null
export const resetCurrentPromotionRequest = () => {
  currentPromotionRequest = null
}
export const ensureCurrentPromotion = () => (dispatch, getState) => {
  if (!currentPromotionRequest)
    currentPromotionRequest = dispatch(getCurrentPromotion())
      .catch(() => null)
      .then(() => getState().app.promotion)
  return currentPromotionRequest
}

const promoPlanPrices = () => {
  const planPrices = plans.map((p) => ({
    id: p.id,
    name: p.name,
    price: p.price
  }))
  planPrices.pop()
  planPrices.push({
    id: LIFETIME_PLAN_ID,
    name: 'Lifetime',
    price: 1199
  })
  return planPrices
}

// SPEC-096: the coupon endpoint is rate limited (25 lookups per user per
// day), and globalPromo() runs on every route change. A code is looked up
// once per page load; route changes reuse that answer. Checkout re-checks
// an answer older than PROMO_RECHECK_AT_CHECKOUT_MS.
export const PROMO_RECHECK_AT_CHECKOUT_MS = 10 * 60 * 1000
export const PROMO_THROTTLE_RETRY_MS = 3000
const OK = 'VALIDATE_GLOBAL_PROMO_OK'
// code → { ok, payload, at }: settled answers for this page load
const promoLookups = new Map()
// code → in-flight lookup, so simultaneous callers share one request
const promoInFlight = new Map()
export const resetPromoLookups = () => {
  promoLookups.clear()
  promoInFlight.clear()
}

const lookupPromo = (dispatch, codename) => {
  if (!promoInFlight.has(codename))
    promoInFlight.set(
      codename,
      dispatch(validateGlobalPromoCode(codename, promoPlanPrices())())
        .then(([_, res]) => res)
        // a 400 (unknown or expired code) is thrown by fetchFailureMiddleware
        .catch(() => ({
          type: 'VALIDATE_GLOBAL_PROMO_FAIL',
          payload: { status: 400 }
        }))
        .finally(() => promoInFlight.delete(codename))
    )
  return promoInFlight.get(codename)
}

const isThrottled = (res) =>
  res && res.type !== OK && res.payload && res.payload.status === 429

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

const reuse = (dispatch, getState, codename, cached) => {
  if (cached.ok && getState().app.globalPromo.codename !== codename)
    dispatch(restoreGlobalPromo(cached.payload))
  return cached.ok ? 'ok' : 'invalid'
}

// 'ok' | 'invalid' | 'throttled'. A 429 is never read as "invalid" (FR-2):
// the last good answer is kept, or the lookup is retried once; it is never
// cached, so the next page tries again.
const validatePromo = async (
  dispatch,
  getState,
  codename,
  { maxAge = Infinity } = {}
) => {
  const cached = promoLookups.get(codename)
  if (cached && Date.now() - cached.at < maxAge)
    return reuse(dispatch, getState, codename, cached)
  let res = await lookupPromo(dispatch, codename)
  if (isThrottled(res)) {
    if (cached && cached.ok) {
      capturePosthogEvent('promo_lookup_throttled', {
        coupon_code: codename,
        recovered: 'cached'
      })
      return reuse(dispatch, getState, codename, cached)
    }
    await wait(PROMO_THROTTLE_RETRY_MS)
    res = await lookupPromo(dispatch, codename)
    capturePosthogEvent('promo_lookup_throttled', {
      coupon_code: codename,
      recovered: isThrottled(res) ? 'no' : 'retry'
    })
    if (isThrottled(res)) return 'throttled'
  }
  const ok = res.type === OK
  const status = res.payload && res.payload.status
  // a network or server error is not an answer about the code
  if (ok || (status >= 400 && status < 500))
    promoLookups.set(codename, { ok, payload: res.payload, at: Date.now() })
  return ok ? 'ok' : 'invalid'
}

// Which promo the site runs with. Precedence (SPEC-092 FR-4): the code in
// the URL, then the promo cookie, then the sitewide promotion running now —
// so during a sale every visitor gets the sale price, even with no link and
// no stored cookie (a private window, another device). A specific code is
// remembered (FR-3); the fallback is never written to the cookie, so it
// lapses with the promotion itself (FR-6). No promotion and no code: full
// prices and no cookie, exactly as before (FR-5).
// SPEC-096: lookups are reused for the page load; checkout re-checks an
// answer older than 10 min, and a code of a promotion that has since
// ended is always looked up again.
export const resolveGlobalPromo = (codename) => async (dispatch, getState) => {
  const { value } = await getGlobalPromoCookie(dispatch)
  const specific = codename || parsePromoCookie(value).codename
  const promotion = await dispatch(ensureCurrentPromotion())
  const fallback = activePromotionCode(promotion)
  const atCheckout =
    typeof window !== 'undefined' && window.location.pathname === '/checkout'
  const maxAgeFor = (code) => {
    const endedPromotionCode =
      code === promotion?.couponPromoCode?.codename && !fallback
    if (endedPromotionCode) return 0
    return atCheckout ? PROMO_RECHECK_AT_CHECKOUT_MS : Infinity
  }
  let throttled = false
  if (specific) {
    const result = await validatePromo(dispatch, getState, specific, {
      maxAge: maxAgeFor(specific)
    })
    if (result === 'ok') {
      await storePromoCode(dispatch, specific, promotion)
      dispatch(setGlobalPromoSource('code'))
      return
    }
    // a throttled lookup says nothing about the code: keep the cookie
    if (result === 'invalid')
      await setGlobalPromoCookie(dispatch, JSON.stringify({}))
    else throttled = true
  }
  if (fallback && fallback !== specific) {
    const result = await validatePromo(dispatch, getState, fallback, {
      maxAge: maxAgeFor(fallback)
    })
    if (result === 'ok') {
      dispatch(setGlobalPromoSource('active_promotion'))
      return
    }
    if (result === 'throttled') throttled = true
  }
  // throttled with nothing good to show: leave the promo state as it is
  if (throttled) {
    if (getState().app.globalPromo.isLoading)
      dispatch(displayStandardSubscriptionPlans())
    return
  }
  dispatch(setGlobalPromoSource(''))
  if (!specific) dispatch(displayStandardSubscriptionPlans())
}

export default (stateProps, dispatchProps, ownProps) => {
  const { dispatch } = dispatchProps
  return {
    ...stateProps,
    ...ownProps,
    clearGlobalNotification() {
      dispatch(clearNotification())
    },
    updateDimensions(width, height) {
      const { isMobile } = stateProps

      if (width <= MOBILE_BREAKPOINT) {
        if (!isMobile) dispatch(setIsMobile())
      } else if (isMobile) dispatch(unsetIsMobile())

      dispatch(updateDimensions({ width, height }))
    },
    proxyLogin(props) {
      detectProxyIPCookie(dispatch).then((data) => {
        if (data.value !== undefined)
          dispatch(dispatchProxyLogin(dispatch, props.location.query, props))
      })
    },
    globalPromo(codename) {
      dispatch(resolveGlobalPromo(codename))
    },
    loadUserInfo() {
      dispatch(getUser())
        .then((res) => {
          if (res[1].type === authTypes.AUTH_FETCH_USER_SUCCESS) {
            const payload = res[1].payload
            if (_.isNumber(payload.subscription)) {
              const subscriptionId = payload.subscription

              dispatch(subscriptionSummaryFetch(subscriptionId)())
            }
            // Check if External Dialog Funnel must be displayed
            if (
              payload?.createdByProxy &&
              !payload?.alternativeEmail &&
              !payload?.declinedNewsletter
            )
              dispatch(openExternalUserFunnelDialog())
          }
        })
        .catch((payload) => {
          const error = payload.errors
          toastServerError(error)
        })
    },
    loadSubscriptionSummary() {
      if (_.has(stateProps, 'subscription.id')) {
        const subscriptionId = stateProps.subscription.id
        if (_.isNumber(subscriptionId))
          dispatch(subscriptionSummaryFetch(subscriptionId)())
      }
    },
    showLoadingBar() {
      dispatch(showLoading())
    },
    hideLoadingBar() {
      dispatch(hideLoading())
    },
    toggleMobileMenu() {
      dispatch(toggleMobileMenu())
    },
    loadCartItems() {
      dispatch(cartItemsFetch())
    },
    loadCurrentPromotion() {
      dispatch(ensureCurrentPromotion())
    },
    updateFullscreenStatus(onFullscreen) {
      if (onFullscreen) dispatch(setFullscreen())
      else dispatch(unsetFullscreen())
    },
    async storeUTM(data) {
      const utmData = _.pick(data, UTM_KEYS)
      const IN_MS_28_DAYS = 2419200000

      // store utm parameters as cookies or refresh old utm cookes (to
      // attribute possible conversion to most recent Facebook ad referer)
      if (!_.isEmpty(utmData)) {
        // TODO: consider not adding utm data to the store to:
        //  - prevent having two sources of truth, and
        //  - make sure that utm data in the frontend gets removed after
        //    28 days.
        dispatch(setUTM(utmData))
        UTM_KEYS.forEach((utmKey) => {
          dispatch(
            cookie(utmKey, data[utmKey], { maxage: IN_MS_28_DAYS, path: '/' })
          )
        })
      } else
        getUTMDataFromCookies(dispatch).then((data) => {
          const result = removeUTMPrefixFromKeysIn(data)

          if (!_.isEmpty(result)) {
            dispatch(setUTM(result))

            if (stateProps.subscription && stateProps.subscription.active)
              dispatch(sendUTM(result)).then(([_, res]) => {
                if (res.type === 'SET_UTM_SUCCESS')
                  UTM_KEYS.forEach((cookieName) => {
                    dispatch(cookie(cookieName, null, { path: '/' }))
                  })
              })
          }
        })
    },
    updateScrollPercentage(percentage) {
      dispatch(updateScrollPercentage(percentage))
    },
    ...bannerHandler(dispatch),
    ...navHandlers(dispatch),
    ...subscriptionPlansDispatcher(dispatch)
  }
}

export const notFoundDispatchers = (dispatch) => ({
  ...navHandlers(dispatch)
})

export const proxySSODispatchers = (dispatch) => ({
  proxyLoginSSODispatcher(props) {
    dispatchProxyLogin(dispatch, props?.location?.query, props)
  }
})

export const openAthensSSODispatchers = (dispatch) => ({
  openAthensSSODispatcher(props) {
    dispatchOpenAthensLogin(dispatch, props?.location?.query, props)
  }
})
